import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  Grid,
  IconButton,
  Card,
  CardContent,
  CardActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { useContent } from '../context/ContentContext';
import { useTranslation } from 'react-i18next';
import ClearIcon from '@mui/icons-material/Clear';

// Styled components
const ContentSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  borderRadius: theme.shape.borderRadius,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
}));

const ContentCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(2),
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
}));

function Content() {
  const { content, updateContent } = useContent();
  const [tabValue, setTabValue] = useState(0);
  const [editedContent, setEditedContent] = useState(content);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const { t } = useTranslation();

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Handle content change
  const handleContentChange = (section, subsection, field, value) => {
    const newContent = { ...editedContent };
    
    if (subsection) {
      // Handle nested field paths like 'cta.primary.text_en'
      if (field.includes('.')) {
        const fieldParts = field.split('.');
        let target = newContent[section][subsection];
        
        // Navigate to the parent object
        for (let i = 0; i < fieldParts.length - 1; i++) {
          if (!target[fieldParts[i]]) {
            target[fieldParts[i]] = {};
          }
          target = target[fieldParts[i]];
        }
        
        // Set the final value
        target[fieldParts[fieldParts.length - 1]] = value;
      } else {
        newContent[section][subsection][field] = value;
      }
    } else {
      // Handle nested field paths at the section level
      if (field.includes('.')) {
        const fieldParts = field.split('.');
        let target = newContent[section];
        
        // Navigate to the parent object
        for (let i = 0; i < fieldParts.length - 1; i++) {
          if (!target[fieldParts[i]]) {
            target[fieldParts[i]] = {};
          }
          target = target[fieldParts[i]];
        }
        
        // Set the final value
        target[fieldParts[fieldParts.length - 1]] = value;
      } else {
        newContent[section][field] = value;
      }
    }
    
    setEditedContent(newContent);
  };

  // Handle array item change
  const handleArrayItemChange = (section, subsection, index, field, value, nestedField) => {
    const newContent = { ...editedContent };
    
    // 处理嵌套字段 (services.items)
    if (nestedField) {
      newContent[section][subsection][nestedField][index][field] = value;
    } else {
      newContent[section][subsection][index][field] = value;
    }
    
    setEditedContent(newContent);
  };

  // Add new item to array
  const handleAddItem = (section, subsection, field) => {
    const newContent = { ...editedContent };
    
    // 处理特殊情况，如 services.items
    if (section === 'home' && subsection === 'services' && field === 'items') {
      const items = [...(newContent[section][subsection][field] || [])];
      
      // 创建新的服务项
      const newItem = {
        title: 'New Service Item',
        description: 'Service item description',
        icon: 'construction',
        path: '/services/new-service-item',
        image: '/images/services/default.jpg',
        fullDescription: 'Detailed service description'
      };
      
      items.push(newItem);
      newContent[section][subsection][field] = items;
    } else {
      // 处理常规情况
      const items = [...newContent[section][subsection]];
      
      // Create a new item based on the section type
      let newItem;
      
      if (section === 'home' && subsection === 'features') {
        newItem = {
          title: 'New Feature',
          description: 'Feature description',
          icon: 'add_circle'
        };
      } else if (section === 'home' && subsection === 'services') {
        newItem = {
          title: 'New Service',
          description: 'Service description',
          icon: 'add_circle',
          path: '/services/new-service'
        };
      } else if (section === 'home' && subsection === 'testimonials') {
        newItem = {
          name: 'New Client',
          title: 'Client Position',
          text: 'Testimonial text',
          rating: 5,
          image: '/images/testimonials/default.jpg'
        };
      } else if (section === 'home' && subsection === 'projects') {
        newItem = {
          title: 'New Project',
          description: 'Project description',
          image: '/images/projects/default.jpg',
          path: '/projects/new-project'
        };
      }
      
      items.push(newItem);
      newContent[section][subsection] = items;
    }
    
    setEditedContent(newContent);
  };

  // Show delete confirmation dialog
  const confirmDelete = (section, subsection, index, field) => {
    setItemToDelete({ section, subsection, index, field });
    setOpenDialog(true);
  };

  // Delete item from array
  const handleDeleteItem = () => {
    if (!itemToDelete) return;
    
    const { section, subsection, index, field } = itemToDelete;
    const newContent = { ...editedContent };
    
    // 处理特殊情况，如 services.items
    if (field) {
      const items = [...newContent[section][subsection][field]];
      items.splice(index, 1);
      newContent[section][subsection][field] = items;
    } else {
      const items = [...newContent[section][subsection]];
      items.splice(index, 1);
      newContent[section][subsection] = items;
    }
    
    setEditedContent(newContent);
    setOpenDialog(false);
    setItemToDelete(null);
  };

  // Save changes
  const handleSaveChanges = () => {
    updateContent(editedContent);
    setOpenSnackbar(true);
  };

  // Add new bilingual content fields
  const handleBilingualContentChange = (section, subsection, field, language, value) => {
    const newContent = { ...editedContent };
    const i18nField = `${field}_${language}`;
    
    if (subsection) {
      newContent[section][subsection][i18nField] = value;
    } else {
      newContent[section][i18nField] = value;
    }
    
    setEditedContent(newContent);
  };
  
  // Add new bilingual item fields
  const handleBilingualArrayItemChange = (section, subsection, index, field, language, value, nestedField) => {
    const newContent = { ...editedContent };
    const i18nField = `${field}_${language}`;
    
    // 处理嵌套字段 (services.items)
    if (nestedField) {
      newContent[section][subsection][nestedField][index][i18nField] = value;
    } else {
      newContent[section][subsection][index][i18nField] = value;
    }
    
    setEditedContent(newContent);
  };

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4 
      }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {t('admin.content')}
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<SaveIcon />}
          onClick={handleSaveChanges}
        >
          {t('admin.save')}
        </Button>
      </Box>
      
      <Paper sx={{ mb: 4, borderRadius: 2 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          centered
        >
          <Tab label={t('admin.dashboard')} />
          <Tab label={t('navigation.home')} />
          <Tab label={t('navigation.services')} />
          <Tab label={t('navigation.about')} />
          <Tab label={t('navigation.projects')} />
          <Tab label={t('navigation.contact')} />
          <Tab label={t('navigation.blog')} />
          <Tab label={t('footer.title')} />
        </Tabs>
      </Paper>
      
      {/* Home Page Content */}
      {tabValue === 1 && (
        <Box>
          {/* Hero Section */}
          <ContentSection>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              {t('home.hero.title')}
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('admin.titleEN')}
                  fullWidth
                  margin="normal"
                  value={editedContent.home.hero.title_en || editedContent.home.hero.title}
                  onChange={(e) => handleBilingualContentChange('home', 'hero', 'title', 'en', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('admin.titleZH')}
                  fullWidth
                  margin="normal"
                  value={editedContent.home.hero.title_zh || ''}
                  onChange={(e) => handleBilingualContentChange('home', 'hero', 'title', 'zh', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('admin.subtitleEN')}
                  fullWidth
                  margin="normal"
                  value={editedContent.home.hero.subtitle_en || editedContent.home.hero.subtitle}
                  onChange={(e) => handleBilingualContentChange('home', 'hero', 'subtitle', 'en', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('admin.subtitleZH')}
                  fullWidth
                  margin="normal"
                  value={editedContent.home.hero.subtitle_zh || ''}
                  onChange={(e) => handleBilingualContentChange('home', 'hero', 'subtitle', 'zh', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('admin.descriptionEN')}
                  fullWidth
                  multiline
                  rows={3}
                  margin="normal"
                  value={editedContent.home.hero.description_en || editedContent.home.hero.description}
                  onChange={(e) => handleBilingualContentChange('home', 'hero', 'description', 'en', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('admin.descriptionZH')}
                  fullWidth
                  multiline
                  rows={3}
                  margin="normal"
                  value={editedContent.home.hero.description_zh || ''}
                  onChange={(e) => handleBilingualContentChange('home', 'hero', 'description', 'zh', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('admin.titleEN') + " - " + t('common.learnMore')}
                  fullWidth
                  margin="normal"
                  value={editedContent.home.hero.cta?.primary?.text || ''}
                  onChange={(e) => handleContentChange('home', 'hero', 'cta.primary.text', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('admin.titleZH') + " - " + t('common.learnMore')}
                  fullWidth
                  margin="normal"
                  value={editedContent.home.hero.cta?.primary?.text_zh || ''}
                  onChange={(e) => handleContentChange('home', 'hero', 'cta.primary.text_zh', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('admin.titleEN') + " - " + t('navigation.contact')}
                  fullWidth
                  margin="normal"
                  value={editedContent.home.hero.cta?.secondary?.text || ''}
                  onChange={(e) => handleContentChange('home', 'hero', 'cta.secondary.text', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('admin.titleZH') + " - " + t('navigation.contact')}
                  fullWidth
                  margin="normal"
                  value={editedContent.home.hero.cta?.secondary?.text_zh || ''}
                  onChange={(e) => handleContentChange('home', 'hero', 'cta.secondary.text_zh', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('admin.primaryButtonPath')}
                  fullWidth
                  margin="normal"
                  value={editedContent.home.hero.cta?.primary?.path || ''}
                  onChange={(e) => handleContentChange('home', 'hero', 'cta.primary.path', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('admin.secondaryButtonPath')}
                  fullWidth
                  margin="normal"
                  value={editedContent.home.hero.cta?.secondary?.path || ''}
                  onChange={(e) => handleContentChange('home', 'hero', 'cta.secondary.path', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t('home.hero.image')}
                  fullWidth
                  margin="normal"
                  value={editedContent.home.hero.image}
                  onChange={(e) => handleContentChange('home', 'hero', 'image', e.target.value)}
                />
              </Grid>
            </Grid>
          </ContentSection>
          
          {/* Features Section */}
          <ContentSection>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('home.features.title')}
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => handleAddItem('home', 'features')}
              >
                {t('admin.addNew')}
              </Button>
            </Box>
            
            {editedContent.home.features.map((feature, index) => (
              <ContentCard key={index}>
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label={t('admin.titleEN')}
                        fullWidth
                        margin="normal"
                        value={feature.title_en || feature.title}
                        onChange={(e) => handleBilingualArrayItemChange('home', 'features', index, 'title', 'en', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label={t('admin.titleZH')}
                        fullWidth
                        margin="normal"
                        value={feature.title_zh || ''}
                        onChange={(e) => handleBilingualArrayItemChange('home', 'features', index, 'title', 'zh', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label={t('admin.descriptionEN')}
                        fullWidth
                        multiline
                        rows={2}
                        margin="normal"
                        value={feature.description_en || feature.description}
                        onChange={(e) => handleBilingualArrayItemChange('home', 'features', index, 'description', 'en', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label={t('admin.descriptionZH')}
                        fullWidth
                        multiline
                        rows={2}
                        margin="normal"
                        value={feature.description_zh || ''}
                        onChange={(e) => handleBilingualArrayItemChange('home', 'features', index, 'description', 'zh', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label={t('home.features.icon')}
                        fullWidth
                        margin="normal"
                        value={feature.icon}
                        onChange={(e) => handleArrayItemChange('home', 'features', index, 'icon', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end' }}>
                  <IconButton 
                    color="error" 
                    onClick={() => confirmDelete('home', 'features', index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </ContentCard>
            ))}
          </ContentSection>
          
          {/* About Section */}
          <ContentSection>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              {t('home.about.title')}
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('admin.titleEN')}
                  fullWidth
                  margin="normal"
                  value={editedContent.home.about.title_en || editedContent.home.about.title}
                  onChange={(e) => handleBilingualContentChange('home', 'about', 'title', 'en', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('admin.titleZH')}
                  fullWidth
                  margin="normal"
                  value={editedContent.home.about.title_zh || ''}
                  onChange={(e) => handleBilingualContentChange('home', 'about', 'title', 'zh', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('admin.subtitleEN')}
                  fullWidth
                  margin="normal"
                  value={editedContent.home.about.subtitle_en || editedContent.home.about.subtitle}
                  onChange={(e) => handleBilingualContentChange('home', 'about', 'subtitle', 'en', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('admin.subtitleZH')}
                  fullWidth
                  margin="normal"
                  value={editedContent.home.about.subtitle_zh || ''}
                  onChange={(e) => handleBilingualContentChange('home', 'about', 'subtitle', 'zh', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('admin.descriptionEN')}
                  fullWidth
                  multiline
                  rows={4}
                  margin="normal"
                  value={editedContent.home.about.description_en || editedContent.home.about.description}
                  onChange={(e) => handleBilingualContentChange('home', 'about', 'description', 'en', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('admin.descriptionZH')}
                  fullWidth
                  multiline
                  rows={4}
                  margin="normal"
                  value={editedContent.home.about.description_zh || ''}
                  onChange={(e) => handleBilingualContentChange('home', 'about', 'description', 'zh', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t('home.about.image')}
                  fullWidth
                  margin="normal"
                  value={editedContent.home.about.image}
                  onChange={(e) => handleContentChange('home', 'about', 'image', e.target.value)}
                />
              </Grid>
            </Grid>
            
            <Typography variant="subtitle1" sx={{ mt: 3, mb: 2, fontWeight: 600 }}>
              {t('common.stats')}
            </Typography>
            
            <Grid container spacing={2}>
              {editedContent.home.about.statistics.map((stat, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <TextField
                    label={t('common.value')}
                    fullWidth
                    margin="normal"
                    value={stat.value}
                    onChange={(e) => handleArrayItemChange('home', 'about', index, 'value', e.target.value, 'statistics')}
                  />
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label={t('admin.labelEN')}
                        fullWidth
                        margin="normal"
                        value={stat.label_en || stat.label}
                        onChange={(e) => handleBilingualArrayItemChange('home', 'about', index, 'label', 'en', e.target.value, 'statistics')}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label={t('admin.labelZH')}
                        fullWidth
                        margin="normal"
                        value={stat.label_zh || ''}
                        onChange={(e) => handleBilingualArrayItemChange('home', 'about', index, 'label', 'zh', e.target.value, 'statistics')}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </ContentSection>

          {/* Services Section */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>{t('admin.servicesSection')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ContentSection>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={`${t('admin.title')} (English)`}
                      fullWidth
                      value={editedContent.home.services.title_en || editedContent.home.services.title}
                      onChange={(e) => handleBilingualContentChange('home', 'services', 'title', 'en', e.target.value)}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={`${t('admin.title')} (中文)`}
                      fullWidth
                      value={editedContent.home.services.title_zh || ''}
                      onChange={(e) => handleBilingualContentChange('home', 'services', 'title', 'zh', e.target.value)}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={`${t('admin.subtitle')} (English)`}
                      fullWidth
                      value={editedContent.home.services.subtitle_en || editedContent.home.services.subtitle}
                      onChange={(e) => handleBilingualContentChange('home', 'services', 'subtitle', 'en', e.target.value)}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={`${t('admin.subtitle')} (中文)`}
                      fullWidth
                      value={editedContent.home.services.subtitle_zh || ''}
                      onChange={(e) => handleBilingualContentChange('home', 'services', 'subtitle', 'zh', e.target.value)}
                      margin="normal"
                    />
                  </Grid>
                </Grid>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4, mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {t('home.services.title')}
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => handleAddItem('home', 'services', 'items')}
                  >
                    {t('admin.addNew')}
                  </Button>
                </Box>
                
                {editedContent.home.services.items && editedContent.home.services.items.map((service, index) => (
                  <ContentCard key={index}>
                    <CardContent>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label={`${t('admin.title')} (English)`}
                            fullWidth
                            value={service.title_en || service.title}
                            onChange={(e) => handleBilingualArrayItemChange('home', 'services', index, 'title', 'en', e.target.value, 'items')}
                            margin="normal"
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label={`${t('admin.title')} (中文)`}
                            fullWidth
                            value={service.title_zh || ''}
                            onChange={(e) => handleBilingualArrayItemChange('home', 'services', index, 'title', 'zh', e.target.value, 'items')}
                            margin="normal"
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label={`${t('admin.shortDescription')} (English)`}
                            fullWidth
                            multiline
                            rows={2}
                            value={service.description_en || service.description}
                            onChange={(e) => handleBilingualArrayItemChange('home', 'services', index, 'description', 'en', e.target.value, 'items')}
                            margin="normal"
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label={`${t('admin.shortDescription')} (中文)`}
                            fullWidth
                            multiline
                            rows={2}
                            value={service.description_zh || ''}
                            onChange={(e) => handleBilingualArrayItemChange('home', 'services', index, 'description', 'zh', e.target.value, 'items')}
                            margin="normal"
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label={t('admin.icon')}
                            fullWidth
                            value={service.icon}
                            onChange={(e) => handleArrayItemChange('home', 'services', index, 'icon', e.target.value, 'items')}
                            margin="normal"
                            helperText={t('admin.materialIconName')}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label={t('admin.linkPath')}
                            fullWidth
                            value={service.path}
                            onChange={(e) => handleArrayItemChange('home', 'services', index, 'path', e.target.value, 'items')}
                            margin="normal"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                            {t('admin.serviceImage')}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            {service.image && (
                              <Box sx={{ mr: 2, position: 'relative' }}>
                                <img 
                                  src={service.image} 
                                  alt={service.title} 
                                  style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 4 }}
                                />
                                <IconButton
                                  size="small"
                                  sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper' }}
                                  onClick={() => handleArrayItemChange('home', 'services', index, 'image', '', 'items')}
                                >
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            )}
                            <TextField
                              fullWidth
                              label={t('admin.imageURL')}
                              value={service.image || ''}
                              onChange={(e) => handleArrayItemChange('home', 'services', index, 'image', e.target.value, 'items')}
                              helperText={t('admin.pasteImageURL')}
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                            {t('admin.detailedContent')}
                          </Typography>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {t('admin.englishContent')}
                            </Typography>
                            <TextField
                              fullWidth
                              multiline
                              rows={4}
                              value={service.fullDescription_en || service.fullDescription || ''}
                              onChange={(e) => handleBilingualArrayItemChange('home', 'services', index, 'fullDescription', 'en', e.target.value, 'items')}
                            />
                          </Box>
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {t('admin.chineseContent')}
                            </Typography>
                            <TextField
                              fullWidth
                              multiline
                              rows={4}
                              value={service.fullDescription_zh || ''}
                              onChange={(e) => handleBilingualArrayItemChange('home', 'services', index, 'fullDescription', 'zh', e.target.value, 'items')}
                            />
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'flex-end' }}>
                      <IconButton 
                        color="error" 
                        onClick={() => confirmDelete('home', 'services', index, 'items')}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </CardActions>
                  </ContentCard>
                ))}
              </ContentSection>
            </AccordionDetails>
          </Accordion>
          
          {/* Testimonials Section */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>{t('admin.testimonialsSection')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ContentSection>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('home.testimonials.title')}
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => handleAddItem('home', 'testimonials')}
                  >
                    {t('admin.addNew')}
                  </Button>
                </Box>
                
                {editedContent.home.testimonials && editedContent.home.testimonials.map((testimonial, index) => (
                  <ContentCard key={index}>
                    <CardContent>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label={t('admin.name')}
                            fullWidth
                            margin="normal"
                            value={testimonial.name}
                            onChange={(e) => handleArrayItemChange('home', 'testimonials', index, 'name', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label={t('admin.position')}
                            fullWidth
                            margin="normal"
                            value={testimonial.title}
                            onChange={(e) => handleArrayItemChange('home', 'testimonials', index, 'title', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label={t('admin.testimonialText') + " (EN)"}
                            fullWidth
                            multiline
                            rows={3}
                            margin="normal"
                            value={testimonial.text_en || testimonial.text}
                            onChange={(e) => handleBilingualArrayItemChange('home', 'testimonials', index, 'text', 'en', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label={t('admin.testimonialText') + " (ZH)"}
                            fullWidth
                            multiline
                            rows={3}
                            margin="normal"
                            value={testimonial.text_zh || ''}
                            onChange={(e) => handleBilingualArrayItemChange('home', 'testimonials', index, 'text', 'zh', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            label={t('admin.rating')}
                            fullWidth
                            type="number"
                            InputProps={{ inputProps: { min: 1, max: 5 } }}
                            margin="normal"
                            value={testimonial.rating}
                            onChange={(e) => handleArrayItemChange('home', 'testimonials', index, 'rating', parseInt(e.target.value, 10) || 5)}
                          />
                        </Grid>
                        <Grid item xs={12} md={8}>
                          <TextField
                            label={t('admin.image')}
                            fullWidth
                            margin="normal"
                            value={testimonial.image}
                            onChange={(e) => handleArrayItemChange('home', 'testimonials', index, 'image', e.target.value)}
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'flex-end' }}>
                      <IconButton 
                        color="error" 
                        onClick={() => confirmDelete('home', 'testimonials', index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </CardActions>
                  </ContentCard>
                ))}
              </ContentSection>
            </AccordionDetails>
          </Accordion>
          
          {/* Projects Section */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>{t('admin.projectsSection')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ContentSection>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t('admin.titleEN')}
                      fullWidth
                      margin="normal"
                      value={editedContent.home.projects.title_en || editedContent.home.projects.title}
                      onChange={(e) => handleBilingualContentChange('home', 'projects', 'title', 'en', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t('admin.titleZH')}
                      fullWidth
                      margin="normal"
                      value={editedContent.home.projects.title_zh || ''}
                      onChange={(e) => handleBilingualContentChange('home', 'projects', 'title', 'zh', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t('admin.subtitleEN')}
                      fullWidth
                      margin="normal"
                      value={editedContent.home.projects.subtitle_en || editedContent.home.projects.subtitle}
                      onChange={(e) => handleBilingualContentChange('home', 'projects', 'subtitle', 'en', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t('admin.subtitleZH')}
                      fullWidth
                      margin="normal"
                      value={editedContent.home.projects.subtitle_zh || ''}
                      onChange={(e) => handleBilingualContentChange('home', 'projects', 'subtitle', 'zh', e.target.value)}
                    />
                  </Grid>
                </Grid>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4, mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {t('home.projects.title')}
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => handleAddItem('home', 'projects', 'items')}
                  >
                    {t('admin.addNew')}
                  </Button>
                </Box>
                
                {editedContent.home.projects.items && editedContent.home.projects.items.map((project, index) => (
                  <ContentCard key={index}>
                    <CardContent>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label={t('admin.titleEN')}
                            fullWidth
                            margin="normal"
                            value={project.title_en || project.title}
                            onChange={(e) => handleBilingualArrayItemChange('home', 'projects', index, 'title', 'en', e.target.value, 'items')}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label={t('admin.titleZH')}
                            fullWidth
                            margin="normal"
                            value={project.title_zh || ''}
                            onChange={(e) => handleBilingualArrayItemChange('home', 'projects', index, 'title', 'zh', e.target.value, 'items')}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label={t('admin.descriptionEN')}
                            fullWidth
                            multiline
                            rows={2}
                            margin="normal"
                            value={project.description_en || project.description}
                            onChange={(e) => handleBilingualArrayItemChange('home', 'projects', index, 'description', 'en', e.target.value, 'items')}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label={t('admin.descriptionZH')}
                            fullWidth
                            multiline
                            rows={2}
                            margin="normal"
                            value={project.description_zh || ''}
                            onChange={(e) => handleBilingualArrayItemChange('home', 'projects', index, 'description', 'zh', e.target.value, 'items')}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                            {t('admin.image')}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            {project.image && (
                              <Box sx={{ mr: 2, position: 'relative' }}>
                                <img 
                                  src={project.image} 
                                  alt={project.title} 
                                  style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 4 }}
                                />
                                <IconButton
                                  size="small"
                                  sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper' }}
                                  onClick={() => handleArrayItemChange('home', 'projects', index, 'image', '', 'items')}
                                >
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            )}
                            <TextField
                              fullWidth
                              label={t('admin.imageURL')}
                              value={project.image || ''}
                              onChange={(e) => handleArrayItemChange('home', 'projects', index, 'image', e.target.value, 'items')}
                              helperText={t('admin.pasteImageURL')}
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label={t('admin.path')}
                            fullWidth
                            margin="normal"
                            value={project.path || ''}
                            onChange={(e) => handleArrayItemChange('home', 'projects', index, 'path', e.target.value, 'items')}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                            {t('admin.detailedContent')}
                          </Typography>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {t('admin.englishContent')}
                            </Typography>
                            <TextField
                              fullWidth
                              multiline
                              rows={4}
                              value={project.fullDescription_en || project.fullDescription || ''}
                              onChange={(e) => handleBilingualArrayItemChange('home', 'projects', index, 'fullDescription', 'en', e.target.value, 'items')}
                            />
                          </Box>
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {t('admin.chineseContent')}
                            </Typography>
                            <TextField
                              fullWidth
                              multiline
                              rows={4}
                              value={project.fullDescription_zh || ''}
                              onChange={(e) => handleBilingualArrayItemChange('home', 'projects', index, 'fullDescription', 'zh', e.target.value, 'items')}
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                            {t('admin.technicalSpecifications')}
                          </Typography>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {t('admin.englishContent')}
                            </Typography>
                            <TextField
                              fullWidth
                              multiline
                              rows={3}
                              value={project.specs || ''}
                              onChange={(e) => handleArrayItemChange('home', 'projects', index, 'specs', e.target.value, 'items')}
                            />
                          </Box>
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {t('admin.chineseContent')}
                            </Typography>
                            <TextField
                              fullWidth
                              multiline
                              rows={3}
                              value={project.specs_zh || ''}
                              onChange={(e) => handleArrayItemChange('home', 'projects', index, 'specs_zh', e.target.value, 'items')}
                            />
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'flex-end' }}>
                      <IconButton 
                        color="error" 
                        onClick={() => confirmDelete('home', 'projects', index, 'items')}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </CardActions>
                  </ContentCard>
                ))}
              </ContentSection>
            </AccordionDetails>
          </Accordion>
          
          {/* Contact Section */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Contact Section</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary">
                The contact section editor would allow you to edit the call-to-action content
                on the home page, including title, subtitle, and button text.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
      
      {/* Other content tabs would be implemented similarly */}
      {tabValue === 2 && (
        <Box sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            {t('admin.contentDashboard')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('admin.welcomeToContentManagement')}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  {t('admin.quickStart')}
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText 
                      primary={t('admin.editHome')} 
                      secondary={t('admin.editHomeDesc')} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary={t('admin.editServices')} 
                      secondary={t('admin.editServicesDesc')} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary={t('admin.manageContent')} 
                      secondary={t('admin.manageContentDesc')} 
                    />
                  </ListItem>
                </List>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  {t('admin.contentTips')}
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText 
                      primary={t('admin.keepItSimple')} 
                      secondary={t('admin.keepItSimpleDesc')} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary={t('admin.useImages')} 
                      secondary={t('admin.useImagesDesc')} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary={t('admin.testChanges')} 
                      secondary={t('admin.testChangesDesc')} 
                    />
                  </ListItem>
                </List>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}
      
      {/* Services Content */}
      {tabValue === 2 && (
        <Box>
          {/* Services Section */}
          <Box sx={{ mb: 3 }}>
            <Typography>{t('admin.servicesSection')}</Typography>
          </Box>
          
          {/* Services Section Title */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label={`${t('admin.title')} (English)`}
                fullWidth
                value={editedContent.home.services.title_en || editedContent.home.services.title}
                onChange={(e) => handleBilingualContentChange('home', 'services', 'title', 'en', e.target.value)}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={`${t('admin.title')} (中文)`}
                fullWidth
                value={editedContent.home.services.title_zh || ''}
                onChange={(e) => handleBilingualContentChange('home', 'services', 'title', 'zh', e.target.value)}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={`${t('admin.subtitle')} (English)`}
                fullWidth
                value={editedContent.home.services.subtitle_en || editedContent.home.services.subtitle}
                onChange={(e) => handleBilingualContentChange('home', 'services', 'subtitle', 'en', e.target.value)}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={`${t('admin.subtitle')} (中文)`}
                fullWidth
                value={editedContent.home.services.subtitle_zh || ''}
                onChange={(e) => handleBilingualContentChange('home', 'services', 'subtitle', 'zh', e.target.value)}
                margin="normal"
              />
            </Grid>
          </Grid>
          
          {/* Services Items */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
              {t('home.services.title')}
            </Typography>
            
            <Button 
              variant="outlined" 
              startIcon={<AddIcon />} 
              onClick={() => handleAddItem('home', 'services', 'items')}
              sx={{ mb: 2 }}
            >
              {t('admin.addNewItem')}
            </Button>
            
            {editedContent.home.services.items && editedContent.home.services.items.map((service, index) => (
              <Card key={index} sx={{ mb: 3, p: 3, position: 'relative' }}>
                <IconButton 
                  size="small"
                  sx={{ position: 'absolute', top: 8, right: 8 }}
                  onClick={() => confirmDelete('home', 'services', index, 'items')}
                >
                  <DeleteIcon />
                </IconButton>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={`${t('admin.title')} (English)`}
                      fullWidth
                      value={service.title_en || service.title}
                      onChange={(e) => handleBilingualArrayItemChange('home', 'services', index, 'title', 'en', e.target.value, 'items')}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={`${t('admin.title')} (中文)`}
                      fullWidth
                      value={service.title_zh || ''}
                      onChange={(e) => handleBilingualArrayItemChange('home', 'services', index, 'title', 'zh', e.target.value, 'items')}
                      margin="normal"
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={`${t('admin.shortDescription')} (English)`}
                      fullWidth
                      multiline
                      rows={2}
                      value={service.description_en || service.description}
                      onChange={(e) => handleBilingualArrayItemChange('home', 'services', index, 'description', 'en', e.target.value, 'items')}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={`${t('admin.shortDescription')} (中文)`}
                      fullWidth
                      multiline
                      rows={2}
                      value={service.description_zh || ''}
                      onChange={(e) => handleBilingualArrayItemChange('home', 'services', index, 'description', 'zh', e.target.value, 'items')}
                      margin="normal"
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t('admin.icon')}
                      fullWidth
                      value={service.icon}
                      onChange={(e) => handleArrayItemChange('home', 'services', index, 'icon', e.target.value, 'items')}
                      margin="normal"
                      helperText={t('admin.materialIconName')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t('admin.linkPath')}
                      fullWidth
                      value={service.path}
                      onChange={(e) => handleArrayItemChange('home', 'services', index, 'path', e.target.value, 'items')}
                      margin="normal"
                    />
                  </Grid>
                  
                  {/* 图片上传 */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                      {t('admin.serviceImage')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      {service.image && (
                        <Box sx={{ mr: 2, position: 'relative' }}>
                          <img 
                            src={service.image} 
                            alt={service.title} 
                            style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 4 }}
                          />
                          <IconButton
                            size="small"
                            sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper' }}
                            onClick={() => handleArrayItemChange('home', 'services', index, 'image', '', 'items')}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                      <TextField
                        fullWidth
                        label={t('admin.imageURL')}
                        value={service.image || ''}
                        onChange={(e) => handleArrayItemChange('home', 'services', index, 'image', e.target.value, 'items')}
                        helperText={t('admin.pasteImageURL')}
                      />
                    </Box>
                  </Grid>
                  
                  {/* 详细内容 */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                      {t('admin.detailedContent')}
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {t('admin.englishContent')}
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={service.fullDescription_en || service.fullDescription || ''}
                        onChange={(e) => handleBilingualArrayItemChange('home', 'services', index, 'fullDescription', 'en', e.target.value, 'items')}
                      />
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {t('admin.chineseContent')}
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={service.fullDescription_zh || ''}
                        onChange={(e) => handleBilingualArrayItemChange('home', 'services', index, 'fullDescription', 'zh', e.target.value, 'items')}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Card>
            ))}
          </Box>
        </Box>
      )}
      
      {tabValue === 3 && (
        <Box sx={{ p: 3 }}>
          <Typography variant="body1">
            Services page content editor would go here.
          </Typography>
        </Box>
      )}
      
      {tabValue === 4 && (
        <Box>
          <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
            {t('admin.projectsSection')}
          </Typography>
          
          <ContentSection>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('admin.titleEN')}
                  fullWidth
                  margin="normal"
                  value={editedContent.home.projects.title_en || editedContent.home.projects.title}
                  onChange={(e) => handleBilingualContentChange('home', 'projects', 'title', 'en', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('admin.titleZH')}
                  fullWidth
                  margin="normal"
                  value={editedContent.home.projects.title_zh || ''}
                  onChange={(e) => handleBilingualContentChange('home', 'projects', 'title', 'zh', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('admin.subtitleEN')}
                  fullWidth
                  margin="normal"
                  value={editedContent.home.projects.subtitle_en || editedContent.home.projects.subtitle}
                  onChange={(e) => handleBilingualContentChange('home', 'projects', 'subtitle', 'en', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('admin.subtitleZH')}
                  fullWidth
                  margin="normal"
                  value={editedContent.home.projects.subtitle_zh || ''}
                  onChange={(e) => handleBilingualContentChange('home', 'projects', 'subtitle', 'zh', e.target.value)}
                />
              </Grid>
            </Grid>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {t('home.projects.title')}
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => handleAddItem('home', 'projects', 'items')}
              >
                {t('admin.addNew')}
              </Button>
            </Box>
            
            {editedContent.home.projects.items && editedContent.home.projects.items.map((project, index) => (
              <ContentCard key={index}>
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label={t('admin.titleEN')}
                        fullWidth
                        margin="normal"
                        value={project.title_en || project.title}
                        onChange={(e) => handleBilingualArrayItemChange('home', 'projects', index, 'title', 'en', e.target.value, 'items')}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label={t('admin.titleZH')}
                        fullWidth
                        margin="normal"
                        value={project.title_zh || ''}
                        onChange={(e) => handleBilingualArrayItemChange('home', 'projects', index, 'title', 'zh', e.target.value, 'items')}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label={t('admin.descriptionEN')}
                        fullWidth
                        multiline
                        rows={2}
                        margin="normal"
                        value={project.description_en || project.description}
                        onChange={(e) => handleBilingualArrayItemChange('home', 'projects', index, 'description', 'en', e.target.value, 'items')}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label={t('admin.descriptionZH')}
                        fullWidth
                        multiline
                        rows={2}
                        margin="normal"
                        value={project.description_zh || ''}
                        onChange={(e) => handleBilingualArrayItemChange('home', 'projects', index, 'description', 'zh', e.target.value, 'items')}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                        {t('admin.image')}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        {project.image && (
                          <Box sx={{ mr: 2, position: 'relative' }}>
                            <img 
                              src={project.image} 
                              alt={project.title} 
                              style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 4 }}
                            />
                            <IconButton
                              size="small"
                              sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper' }}
                              onClick={() => handleArrayItemChange('home', 'projects', index, 'image', '', 'items')}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                        <TextField
                          fullWidth
                          label={t('admin.imageURL')}
                          value={project.image || ''}
                          onChange={(e) => handleArrayItemChange('home', 'projects', index, 'image', e.target.value, 'items')}
                          helperText={t('admin.pasteImageURL')}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label={t('admin.path')}
                        fullWidth
                        margin="normal"
                        value={project.path || ''}
                        onChange={(e) => handleArrayItemChange('home', 'projects', index, 'path', e.target.value, 'items')}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                        {t('admin.detailedContent')}
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {t('admin.englishContent')}
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          value={project.fullDescription_en || project.fullDescription || ''}
                          onChange={(e) => handleBilingualArrayItemChange('home', 'projects', index, 'fullDescription', 'en', e.target.value, 'items')}
                        />
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {t('admin.chineseContent')}
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          value={project.fullDescription_zh || ''}
                          onChange={(e) => handleBilingualArrayItemChange('home', 'projects', index, 'fullDescription', 'zh', e.target.value, 'items')}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                        {t('admin.technicalSpecifications')}
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {t('admin.englishContent')}
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          value={project.specs || ''}
                          onChange={(e) => handleArrayItemChange('home', 'projects', index, 'specs', e.target.value, 'items')}
                        />
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {t('admin.chineseContent')}
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          value={project.specs_zh || ''}
                          onChange={(e) => handleArrayItemChange('home', 'projects', index, 'specs_zh', e.target.value, 'items')}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end' }}>
                  <IconButton 
                    color="error" 
                    onClick={() => confirmDelete('home', 'projects', index, 'items')}
                  >
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </ContentCard>
            ))}
          </ContentSection>
        </Box>
      )}
      
      {tabValue === 5 && (
        <Box sx={{ p: 3 }}>
          <Typography variant="body1">
            Contact page content editor would go here.
          </Typography>
        </Box>
      )}

      {/* Dialog for delete confirmation */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
      >
        <DialogTitle>{t('admin.confirmDelete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('admin.confirmDelete')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="primary">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDeleteItem} color="error" autoFocus>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for save confirmation */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert 
          onClose={() => setOpenSnackbar(false)} 
          severity="success"
        >
          {t('admin.saveChanges')}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Content; 